// import components
import {
  Box,
  ButtonBase,
  Chip,
  Collapse,
  Paper,
  Typography
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import PrescriptionRulesGrid from './PrescriptionRulesGrid';

// import external libraries
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import types
import type { PrescriptionResponse, PrescriptionStatus } from 'generated/index';

// import constants
import { AMBER, GRAY, PRIMARY } from 'constants/colors';

// import styles
import {
  cardSx,
  statusBarSx,
  topRowSx,
  descriptionIconSx,
  statusChipSx,
  urgencyChipSx,
  metaRowSx,
  metaItemSx,
  smallIconSx,
  chevronBoxSx,
  chevronAnimatedSx,
  expandedGridSx
} from './PrescriptionCard.styles';
import { convertToFormattedDate } from 'utils/date';

interface PrescriptionCardProps {
  prescription: PrescriptionResponse;
  dateInterval?: { startDate: Date; endDate: Date };
}

const statusBarColor = (status: PrescriptionStatus): string => {
  switch (status) {
    case 'ACTIVE':
      return PRIMARY;
    case 'CONCEPT':
      return AMBER;
    default:
      return GRAY;
  }
};

const formatDate = (date?: string) => {
  if (!date) return '?';
  return convertToFormattedDate(date);
};

const PrescriptionCard = ({
  prescription,
  dateInterval
}: PrescriptionCardProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'careReceiversHome.prescriptions'
  });

  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const barColor = statusBarColor(prescription.status);
  const ruleCount = prescription.prescriptionRules?.length ?? 0;
  const hasStartDate = !!prescription.startDate;
  const hasRules = ruleCount > 0 && hasStartDate;

  return (
    <li>
      <Paper variant="outlined" sx={cardSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: hasRules ? 'pointer' : 'default'
          }}
          onClick={hasRules ? toggleExpanded : undefined}
        >
          <Box sx={statusBarSx(barColor)} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={topRowSx}>
              <DescriptionOutlinedIcon sx={descriptionIconSx} />
              <Typography variant="body1" fontWeight={600} component="span">
                {prescription.name}
              </Typography>
              <Chip
                label={t(`status.${prescription.status.toLowerCase()}`)}
                size="small"
                variant="outlined"
                sx={statusChipSx(prescription.status)}
              />

              {prescription.urgency === 'URGENT' && (
                <Chip
                  icon={<WarningAmberOutlinedIcon />}
                  label={t('urgency.urgent')}
                  size="small"
                  variant="outlined"
                  sx={urgencyChipSx}
                />
              )}
            </Box>

            <Box sx={metaRowSx}>
              <Box sx={metaItemSx}>
                <CalendarTodayOutlinedIcon sx={smallIconSx} />
                <Typography variant="body2">
                  {hasStartDate ? (
                    `${formatDate(prescription.startDate)} - ${formatDate(
                      prescription.endDate
                    )}`
                  ) : (
                    <em>{t('noStartDate')}</em>
                  )}
                </Typography>
              </Box>

              <Box sx={metaItemSx}>
                <AccessTimeOutlinedIcon sx={smallIconSx} />
                <Typography variant="body2">
                  {ruleCount} {t('rules', { count: ruleCount })}
                </Typography>
              </Box>
            </Box>
          </Box>

          {hasRules && (
            <ButtonBase focusRipple>
              <Box sx={chevronBoxSx}>
                <KeyboardArrowDownIcon sx={chevronAnimatedSx(expanded)} />
              </Box>
            </ButtonBase>
          )}
        </Box>

        {hasRules && (
          <Collapse in={expanded} unmountOnExit>
            <Box sx={expandedGridSx}>
              <PrescriptionRulesGrid
                prescription={prescription}
                dateInterval={dateInterval}
              />
            </Box>
          </Collapse>
        )}
      </Paper>
    </li>
  );
};

export default memo(PrescriptionCard);
